import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ConfirmModal from '../components/common/ConfirmModal';
import { AttendanceService } from '../services/attendance.service';
import { useHeaderSearch } from '../contexts/SearchContext';
import TimeInput12h from '../components/common/TimeInput12h';

const STATUS_OPTIONS = [
  { value: 'Present', label: 'Present' },
  { value: 'Absent', label: 'Absent' },
  { value: 'HalfDay', label: 'Half Day' },
  { value: 'WFH', label: 'Work From Home' },
  { value: 'Holiday', label: 'Holiday' },
  { value: 'Leave', label: 'Leave' },
];

const TIME_DEFAULTS = {
  Present: { intime: '09:00', outtime: '18:00', breaktime: '01:00' },
  WFH: { intime: '09:00', outtime: '18:00', breaktime: '01:00' },
  HalfDay: { intime: '09:00', outtime: '13:00', breaktime: '00:30' },
  Absent: { intime: '', outtime: '', breaktime: '' },
  Holiday: { intime: '', outtime: '', breaktime: '' },
  Leave: { intime: '', outtime: '', breaktime: '' },
};

const ManualMarkAttendance = () => {
  const { headerSearch } = useHeaderSearch();
  const [employees, setEmployees] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('Present');
  const [intime, setIntime] = useState('09:00');
  const [outtime, setOuttime] = useState('18:00');
  const [breaktime, setBreaktime] = useState('01:00');
  const [reason, setReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    const defaults = TIME_DEFAULTS[status];
    setIntime(defaults.intime);
    setOuttime(defaults.outtime);
    setBreaktime(defaults.breaktime);
  }, [status]);

  const fetchEmployees = async () => {
    try {
      setFetchingEmployees(true);
      const data = await AttendanceService.getEmployeesForSelection();
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setFetchingEmployees(false);
    }
  };

  const combinedSearch = headerSearch || searchQuery;

  const filteredEmployees = useMemo(() => {
    if (!combinedSearch.trim()) return employees;
    const q = combinedSearch.toLowerCase();
    return employees.filter(
      (emp) =>
        emp.first_name?.toLowerCase().includes(q) ||
        emp.last_name?.toLowerCase().includes(q) ||
        emp.email_address?.toLowerCase().includes(q) ||
        emp.empcode?.toLowerCase().includes(q)
    );
  }, [employees, combinedSearch]);

  const pagination = useListPagination(filteredEmployees, combinedSearch);

  const allSelected = employees.length > 0 && selectedEmails.length === employees.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(employees.map((e) => e.email_address));
    }
  };

  const toggleEmployee = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const showTimeFields = ['Present', 'WFH', 'HalfDay'].includes(status);

  const canSubmit = selectedEmails.length > 0 && date && status && reason.trim();

  const handleSubmit = async () => {
    setShowConfirm(false);
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        employees: allSelected ? 'all' : selectedEmails,
        date,
        status,
        reason,
      };
      if (showTimeFields) {
        payload.intime = intime;
        payload.outtime = outtime;
        payload.breaktime = breaktime;
      }
      const data = await AttendanceService.adminMarkAttendance(payload);
      setResult(data.summary);
    } catch (error) {
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-500 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3">
          <ClipboardDocumentCheckIcon className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">Mark Attendance</h1>
            <p className="text-emerald-100 mt-1">
              Mark attendance for all or selected employees
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Configuration */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <Card.Body className="space-y-4">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time fields */}
              {showTimeFields && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      In Time <span className="text-gray-400 font-normal">(12-hour)</span>
                    </label>
                    <TimeInput12h value={intime} onChange={setIntime} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Out Time <span className="text-gray-400 font-normal">(12-hour)</span>
                    </label>
                    <TimeInput12h value={outtime} onChange={setOuttime} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Break Time <span className="text-gray-400 font-normal">(12-hour)</span>
                    </label>
                    <TimeInput12h value={breaktime} onChange={setBreaktime} />
                  </div>
                </>
              )}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Enter reason for marking attendance..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                />
              </div>

              {/* Submit */}
              <Button
                variant="success"
                size="lg"
                className="w-full"
                disabled={!canSubmit}
                loading={loading}
                onClick={() => setShowConfirm(true)}
              >
                Mark Attendance ({selectedEmails.length} employee{selectedEmails.length !== 1 ? 's' : ''})
              </Button>
            </Card.Body>
          </Card>

          {/* Result card */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <Card.Body>
                  {result.error ? (
                    <div className="flex items-center space-x-3 text-red-600">
                      <ExclamationCircleIcon className="h-6 w-6 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Error</p>
                        <p className="text-sm">{result.error}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircleIcon className="h-6 w-6" />
                        <p className="font-medium">Attendance Marked Successfully</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-green-600">{result.marked}</p>
                          <p className="text-xs text-green-700">Marked</p>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-yellow-600">{result.skipped}</p>
                          <p className="text-xs text-yellow-700">Skipped</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                          <p className="text-xs text-red-700">Errors</p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Right: Employee selection */}
        <div className="lg:col-span-2">
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Select Employees</h2>
                <span className="text-sm text-gray-500">
                  {selectedEmails.length} of {employees.length} selected
                </span>
              </div>
            </Card.Header>
            <Card.Body className="space-y-3">
              {/* Select All + Search */}
              <div className="flex items-center gap-3">
                <label className="flex items-center space-x-2 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Select All</span>
                </label>
                {/* <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  />
                </div> */}
              </div>

              <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
              {/* Employee list */}
              <div className="border border-gray-200 rounded-lg max-h-[500px] overflow-y-auto divide-y divide-gray-100">
                {fetchingEmployees ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading employees...
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No employees found
                  </div>
                ) : (
                  pagination.items.map((emp) => {
                    const isSelected = selectedEmails.includes(emp.email_address);
                    return (
                      <label
                        key={emp.email_address}
                        className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleEmployee(emp.email_address)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {emp.first_name} {emp.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {emp.email_address}
                            {emp.empcode ? ` · ${emp.empcode}` : ''}
                            {emp.designation ? ` · ${emp.designation}` : ''}
                          </p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <ListPagination {...pagination} label="employees for attendance" disabled={fetchingEmployees || loading} />
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSubmit}
        title="Confirm Mark Attendance"
        message={`Mark ${selectedEmails.length} employee${selectedEmails.length !== 1 ? 's' : ''} as "${status}" for ${date}?`}
        confirmText="Mark Attendance"
        cancelText="Cancel"
        variant="info"
      />
    </div>
  );
};

export default ManualMarkAttendance;
